import type { Analysis, Horizon } from "../api/types";
import ConfidenceBar from "./ConfidenceBar";
import StanceBadge from "./StanceBadge";

const HORIZON_LABELS: Record<keyof Analysis["horizons"], string> = {
  intraday: "Intraday (~1 day)",
  short: "Short (≤1 week)",
  medium: "Medium (1–3 mo)",
  long: "Long (6–12 mo)",
};

function HorizonRow({ label, h }: { label: string; h: Horizon }) {
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-950/40 p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="text-xs uppercase tracking-wide text-slate-400">{label}</div>
        <StanceBadge stance={h.stance} />
      </div>
      <ConfidenceBar value={h.confidence} />
      <p className="mt-2 text-sm text-slate-200">{h.rationale}</p>
      {h.key_risks.length > 0 && (
        <ul className="mt-2 list-disc space-y-0.5 pl-5 text-xs text-slate-400">
          {h.key_risks.map((r, i) => (
            <li key={i}>{r}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function AnalysisCard({
  analysis,
  onRefresh,
  refreshing,
  cooldownMessage,
}: {
  analysis: Analysis | null;
  onRefresh: () => void;
  refreshing: boolean;
  cooldownMessage?: string | null;
}) {
  const buttonDisabled = refreshing || !!cooldownMessage;
  return (
    <section className="rounded-2xl border border-slate-800 bg-slate-900 p-4">
      <header className="mb-3 flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm font-medium text-slate-300">Analysis</div>
          {analysis && (
            <div className="truncate text-[11px] text-slate-500">
              {new Date(analysis.generated_at).toLocaleString()}
            </div>
          )}
        </div>
        <button
          onClick={onRefresh}
          disabled={buttonDisabled}
          className="shrink-0 whitespace-nowrap rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-200 hover:border-sky-500 hover:text-sky-300 disabled:opacity-50"
        >
          {refreshing ? "Queued…" : analysis ? "Re-analyze" : "Analyze now"}
        </button>
      </header>

      {cooldownMessage && (
        <div className="mb-3 rounded-md border border-amber-900/60 bg-amber-950/30 px-3 py-2 text-xs text-amber-200">
          {cooldownMessage}
        </div>
      )}

      {analysis === null ? (
        refreshing ? (
          <p className="text-sm text-slate-400">
            <span className="mr-2 inline-block h-2 w-2 animate-pulse rounded-full bg-sky-400 align-middle" />
            Generating first analysis — this usually takes 5–15 seconds.
          </p>
        ) : (
          <p className="text-sm text-slate-500">
            No analysis yet. Click <em>Analyze now</em> above to generate one.
          </p>
        )
      ) : (
        <div className="space-y-3">
          {analysis.overall_summary && (
            <p className="text-sm italic text-slate-300">{analysis.overall_summary}</p>
          )}
          <div className={`grid gap-2 ${analysis.horizons.intraday ? "sm:grid-cols-2 lg:grid-cols-4" : "sm:grid-cols-3"}`}>
            {analysis.horizons.intraday && (
              <HorizonRow label={HORIZON_LABELS.intraday} h={analysis.horizons.intraday} />
            )}
            <HorizonRow label={HORIZON_LABELS.short} h={analysis.horizons.short} />
            <HorizonRow label={HORIZON_LABELS.medium} h={analysis.horizons.medium} />
            <HorizonRow label={HORIZON_LABELS.long} h={analysis.horizons.long} />
          </div>
          {analysis.horizons.intraday && (
            <p className="text-[11px] text-amber-400/80">
              Intraday is experimental — Yahoo data has 5–15 min delay; treat as a calibration check, not a day-trading signal.
            </p>
          )}
          {analysis.catalysts.length > 0 && (
            <div className="rounded-xl border border-slate-800 bg-slate-950/40 p-3">
              <div className="mb-1 text-xs uppercase tracking-wide text-slate-400">Catalysts</div>
              <ul className="list-disc space-y-0.5 pl-5 text-xs text-slate-300">
                {analysis.catalysts.map((c, i) => (
                  <li key={i}>{c}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <p className="mt-3 text-[11px] text-amber-400/80">
        Research only. Not financial advice. The model can be wrong; verify with primary sources before acting.
      </p>
    </section>
  );
}
